import React, { useState, useEffect, useRef } from "react";
import "../../styles/OTPVerification.css";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";

const OTPVerification = () => {
  const baseUrl = process.env.REACT_APP_BASE_URL; // Ensure this is correctly configured in your .env
  const location = useLocation();
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [timeLeft, setTimeLeft] = useState(120);
  const [email, setEmail] = useState(location.state?.email || "");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const inputRefs = useRef([]);
  const navigate = useNavigate();

  // Get email from navigation state or redirect if missing
  useEffect(() => {
    // Check if email is available from previous navigation state
    if (!location.state?.email) {
      // If not, redirect back to signup with an error message
      navigate("/signup", {
        state: { error: "Please register first to verify your email." },
      });
    }
    // Also, ensure the email state is set from location.state if it exists
    if (location.state?.email && email === "") {
      // Only update if email is not already set
      setEmail(location.state.email);
    }
  }, [location.state, navigate, email]); // Added email to dependencies to avoid re-runs if it changes

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
      .toString()
      .padStart(2, "0");
    const secs = (seconds % 60).toString().padStart(2, "0");
    return `${mins}:${secs}`;
  };

  // Timer countdown
  useEffect(() => {
    // If timeLeft is 0 or less, stop the timer
    if (timeLeft <= 0) return;

    // Set up a timeout for 1 second
    const timer = setTimeout(() => {
      setTimeLeft(timeLeft - 1); // Decrement timeLeft
    }, 1000);

    // Clean up the timer when the component unmounts or timeLeft changes
    return () => clearTimeout(timer);
  }, [timeLeft]); // Re-run effect whenever timeLeft changes

  // Handle OTP input change
  const handleChange = (index, value) => {
    if (!/^\d*$/.test(value)) return; // Only allow numbers

    const newOtp = [...otp]; // Create a mutable copy of the OTP array
    newOtp[index] = value; // Update the digit at the specific index
    setOtp(newOtp); // Update the state

    // Auto focus to next input if a digit was entered and it's not the last input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus(); // Use optional chaining in case ref is not ready
    }
  };

  // Handle backspace key
  const handleKeyDown = (index, e) => {
    // If backspace is pressed and the current input is empty, move focus to the previous input
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus(); // Use optional chaining
    }
  };

  // Handle paste
  const handlePaste = (e) => {
    e.preventDefault(); // Prevent default paste behavior
    // Get paste data and take only the first 6 characters
    const pasteData = e.clipboardData.getData("text/plain").slice(0, 6);

    // Check if pasted data consists only of digits
    if (/^\d+$/.test(pasteData)) {
      const newOtp = [...otp];
      for (let i = 0; i < pasteData.length; i++) {
        if (i < 6) {
          // Ensure we don't go out of bounds of our 6 inputs
          newOtp[i] = pasteData[i];
        }
      }
      setOtp(newOtp); // Update OTP state
      // Move focus to the last filled input, or the last input if all are filled
      inputRefs.current[Math.min(pasteData.length - 1, 5)]?.focus();
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault(); // Prevent default form submission behavior
    const otpCode = otp.join(""); // Join array elements to form the full OTP string

    // Client-side validation for OTP length
    if (otpCode.length !== 6) {
      setError("Please enter a complete 6-digit code.");
      return; // Stop submission if validation fails
    }

    setIsLoading(true); // Set loading state
    setError(""); // Clear any previous errors

    try {
      // API call to verify OTP
      const response = await axios.post(
        `${baseUrl}/api/v1/auth/verify-otp`,
        { otpCode, email },
        { withCredentials: true }
      );

      console.log(response.status); // CRITICAL DEBUG LOG

      // Check the success status from the backend response
      if (response.status === 200) {
        setIsVerified(true); // Set verified state to true (will trigger success message render)
        // Redirect after a short delay for user to see success message
        setTimeout(() => {
          navigate("/dashboard"); // Navigate to the dashboard
        }, 2000); // 2-second delay
      } else {
        // If backend reports non-success, display backend's message or a generic one
        setError(
          response.data.message || "OTP verification failed. Please try again."
        );
      }
    } catch (err) {
      // Handle network errors or errors from the backend response structure
      console.error(
        "OTP Verification failed:",
        err.response?.data || err.message || err
      ); // Log full error
      setError(
        err.response?.data?.message ||
          "An unexpected error occurred during verification. Please check your network."
      );
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  // Handle resend OTP
  const handleResend = async () => {
    // Prevent resending if timer is still active
    if (timeLeft > 0) {
      setError(`Please wait ${formatTime(timeLeft)} before resending.`);
      return;
    }

    setError(""); // Clear previous errors
    setIsLoading(true); // Set loading state

    try {
      console.log("Resending OTP for email:", email); // Debug log
      const response = await axios.post(
        `${baseUrl}/api/v1/auth/request-otp`, // Ensure correct endpoint for resend
        { email, purpose: "resend-verification" }, // Include purpose if your backend uses it
        { withCredentials: true }
      );

      if (response.data.success) {
        setTimeLeft(120); // Reset timer
        setOtp(["", "", "", "", "", ""]); // Clear OTP inputs
        inputRefs.current[0]?.focus(); // Focus the first input field
        setError("New OTP sent successfully! Please check your email/SMS."); // Success message for resend
      } else {
        setError(
          response.data.message || "Failed to resend OTP. Please try again."
        );
      }
    } catch (err) {
      console.error(
        "Resend OTP failed:",
        err.response?.data || err.message || err
      ); // Log full error
      setError(
        err.response?.data?.message || "Failed to resend OTP due to an error."
      );
    } finally {
      setIsLoading(false); // Reset loading state
    }
  };

  // Conditional rendering for successful verification
  if (isVerified) {
    return (
      <div className="verification-success">
        <h2>Verification Successful! 🎉</h2>
        <p>Your account has been verified.</p>
        <p>Redirecting to your dashboard...</p>
      </div>
    );
  }

  return (
    <div className="otp-container">
      <div className="otp-card">
        <h1>OTP Verification</h1>
        <p className="email-notice">
          We've sent a code to{" "}
          <span className="email-masked">
            {email ? email.replace(/(.{2}).*(@.*)/, "$1****$2") : "your email"}
          </span>
        </p>

        <form onSubmit={handleSubmit}>
          <div className="otp-inputs">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                maxLength="1"
                value={digit}
                onChange={(e) => handleChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                onPaste={handlePaste}
                ref={(el) => (inputRefs.current[index] = el)}
                className="otp-input"
                // autoFocus only on the first input, or if coming back from resend and first is empty
                autoFocus={index === 0 && !otp[0]}
                disabled={isLoading} // Disable inputs during loading
              />
            ))}
          </div>

          {error && <p className="error-message">{error}</p>}

          <p className="timer">
            This code will expire in {formatTime(timeLeft)}
          </p>

          <button type="submit" className="verify-button" disabled={isLoading}>
            {isLoading ? "Verifying..." : "Verify"}
          </button>
        </form>

        <div className="links">
          <p>
            Not my Email Address?{" "}
            <a href="/signup" className="link">
              {" "}
              {/* Changed to Link for proper navigation */}
              Change
            </a>
          </p>
          <p>
            Didn't receive the email?{" "}
            <button // Changed to button for semantic correctness for click handler
              className={`link ${timeLeft > 0 || isLoading ? "disabled" : ""}`}
              onClick={handleResend}
              disabled={timeLeft > 0 || isLoading} // Disabled if timer is still running or loading
            >
              Resend
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default OTPVerification;
